import React from 'react';
import { Loader2, RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';

const Loader = ({ 
  isVisible = false, 
  message = "Loading...", 
  variant = "spinner", // spinner, pulse, dots, refresh
  size = "medium", // small, medium, large
  overlay = true, // true for modal overlay, false for inline
  backgroundColor = "bg-black/50", // overlay background
  className = "",
  onCancel = null, // optional cancel function
  showCancelButton = false,
  type = "loading" // loading, success, error
}) => {
  if (!isVisible) return null;

  // Size configurations
  const sizeConfig = {
    small: {
      spinner: "w-6 h-6",
      text: "text-sm",
      container: "p-4"
    },
    medium: {
      spinner: "w-8 h-8",
      text: "text-base",
      container: "p-6"
    },
    large: {
      spinner: "w-12 h-12",
      text: "text-lg",
      container: "p-8"
    }
  };

  // Loader variants
  const renderLoader = () => {
    const spinnerSize = sizeConfig[size].spinner;
    
    switch (variant) {
      case "pulse":
        return (
          <div className={`${spinnerSize} bg-blue-500 rounded-full animate-pulse`}></div>
        );
      
      case "dots":
        return (
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        );
      
      case "refresh":
        return <RefreshCw className={`${spinnerSize} text-blue-500 animate-spin`} />;
      
      case "spinner":
      default:
        return <Loader2 className={`${spinnerSize} text-blue-500 animate-spin`} />;
    }
  };

  // Type-based icons and colors
  const getTypeConfig = () => {
    switch (type) {
      case "success":
        return {
          icon: <CheckCircle className={`${sizeConfig[size].spinner} text-green-500`} />,
          textColor: "text-green-700",
          bgColor: "bg-green-50"
        };
      case "error":
        return {
          icon: <XCircle className={`${sizeConfig[size].spinner} text-red-500`} />,
          textColor: "text-red-700",
          bgColor: "bg-red-50"
        };
      case "loading":
      default:
        return {
          icon: renderLoader(),
          textColor: "text-gray-700",
          bgColor: "bg-white"
        };
    }
  };

  const typeConfig = getTypeConfig();

  // Loader content
  const LoaderContent = () => (
    <div className={`
      ${typeConfig.bgColor} 
      rounded-lg shadow-lg 
      ${sizeConfig[size].container} 
      flex flex-col items-center justify-center 
      min-w-[200px] max-w-sm mx-4
      ${className}
    `}>
      {/* Icon/Spinner */}
      <div className="mb-4">
        {typeConfig.icon}
      </div>
      
      {/* Message */}
      {message && (
        <p className={`${sizeConfig[size].text} ${typeConfig.textColor} text-center font-medium mb-2`}>
          {message}
        </p>
      )}
      
      {/* Cancel Button */}
      {showCancelButton && onCancel && type === "loading" && (
        <button
          onClick={onCancel}
          className="mt-4 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );

  // Render with or without overlay
  if (overlay) {
    return (
      <div className={`
        fixed inset-0 z-50 flex items-center justify-center 
        ${backgroundColor}
        backdrop-blur-sm
      `}>
        <LoaderContent />
      </div>
    );
  }

  // Inline loader (no overlay)
  return (
    <div className="flex items-center justify-center w-full">
      <LoaderContent />
    </div>
  );
};

export default Loader;
